import { FormTemplate } from "../../../../types/forms";
import ContactSection from "../../../../components/consultantformsections/ContactSection";
import MedicalSection from "../../../../components/consultantformsections/MedicalSection";
import QuestionSection from "../../../../components/consultantformsections/QuestionSection";
import PolicySection from "../../../../components/consultantformsections/PolicySection";
import DisclaimerSection from "../../../../components/consultantformsections/DisclaimerSection";
import SignatureBox from "../../../../components/consultantformsections/ClientSignature";
import MarketingDetailsSection from "../../../../components/consultantformsections/MarketingDetailsSection";
import HeadingSection from "../../../../components/consultantformsections/Heading-Section";
import TextBlockSection from "../../../../components/consultantformsections/TextBlockSection";
import { userId } from "../../../../config";
import { Button } from "../../../../components/ui/Button";

interface UserPreviewProps {
  template: FormTemplate;
  formValues?: Record<string, any>;
  onFormValueChange?: (sectionId: string | number, value: any) => void;
  showSubmit?: boolean;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export default function UserPreview({
  template,
  formValues = {},
  onFormValueChange,
  showSubmit = true,
  onSubmit,
  readOnly = false
}: UserPreviewProps) {
  return (
    <div className="py-4 md:py-8 space-y-4">
      {/* Header Section */}
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-t-[6px] border-[#885ABB] overflow-hidden">
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex justify-between items-start">
            <img
              src="/glossa-purple.png"
              alt="Glossa Logo"
              className="h-7 max-w-[120px] opacity-90 transition-opacity hover:opacity-100 pointer-events-none select-none"
            />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {template.title || "Consultation Form"}
            </h1>
            {template.description && (
              <p className="text-gray-500 text-[15px] leading-relaxed max-w-prose">
                {template.description}
              </p>
            )}
          </div>

          {!readOnly && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-red-500 font-medium text-xs">
                <span className="text-sm -mt-0.5">*</span>
                <span>Indicates required question</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Sections */}
      <div className="max-w-2xl mx-auto space-y-4 pb-12">
        {template.sections?.length ? (
          template.sections.map((section, index) => (
            <div
              key={section.id || index}
              className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] border-l-2 border-l-transparent hover:border-l-[#885ABB]"
            >
              {section.type === "question" && (
                <QuestionSection
                  userId={userId}
                  templateId={template.id}
                  isEditable={false}
                  readOnly={readOnly}
                  fields={section.fields}
                  value={formValues[section.id]}
                  onChange={(val) => onFormValueChange?.(section.id, val)}
                />
              )}
              {section.type === "contact" && (
                <ContactSection
                  userId={userId}
                  templateId={template.id}
                  isEditable={false}
                  readOnly={readOnly}
                  fields={section.fields}
                  value={formValues[section.id]}
                  onChange={(val) => onFormValueChange?.(section.id, val)}
                />
              )}
              {section.type === "medical" && (
                <MedicalSection
                  userId={userId}
                  templateId={template.id}
                  isEditable={false}
                  readOnly={readOnly}
                  fields={section.fields}
                  value={formValues[section.id]}
                  onChange={(val) => onFormValueChange?.(section.id, val)}
                />
              )}
              {section.type === "policy" && (
                <PolicySection userId={userId} templateId={template.id} isEditable={false} fields={section.fields} />
              )}
              {section.type === "disclaimer" && (
                <DisclaimerSection userId={userId} templateId={template.id} isEditable={false} fields={section.fields} />
              )}
              {section.type === "marketing" && (
                <MarketingDetailsSection
                  userId={userId}
                  templateId={template.id}
                  readOnly={readOnly}
                  fields={section.fields}
                  value={formValues[section.id]}
                  onChange={(val) => onFormValueChange?.(section.id, val)}
                />
              )}
              {section.type === "heading" && (
                <HeadingSection userId={userId} templateId={template.id} fields={section.fields} sectionTitle={section.title} />
              )}
              {section.type === "text" && (
                <TextBlockSection userId={userId} templateId={template.id} fields={section.fields} sectionTitle={section.title} />
              )}
            </div>
          ))
        ) : (
          <div className="bg-white p-12 text-center rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-400 italic text-sm">No sections added yet.</p>
          </div>
        )}

        {/* Signature Section */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] border-l-2 border-l-transparent hover:border-l-[#885ABB]">
          <SignatureBox
            value={formValues["signature"]}
            onSave={(val) => onFormValueChange?.("signature", val)}
            isReadOnly={readOnly}
          />
        </div>

        {/* Submit Actions */}
        {showSubmit && !readOnly && (
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-400">
              Please ensure all required fields are filled before submitting.
            </p>
            <Button
              onClick={onSubmit}
              className="w-full sm:w-auto bg-[#885ABB] hover:bg-[#764da3] text-white rounded-xl px-12 py-6 text-base font-bold shadow-lg shadow-purple-100 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Submit Consultation Form
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
